import React, { useState, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'

import Tree from '../../../tree/Tree'
import { GenNode } from '../../../../types/carbon/models/GenNode'
import { MultiTableConfigTable } from '../../../../types/gotypes'
import { getNodeTitle } from '../../../../utils/nodeTypes'
import { getInitialExpandedIds } from '../../../../utils/tree'
import { getAugementedTOC } from '../../../../utils/multitables'
import { API_BASE_URL } from '../../../../constants/system'
import { tableFullPath } from './wizardHelpers'
import { DashboardJobSelect } from '../../DashboardJobs'

export interface TableSourcePickerProps {
  customer: string
  job: string
  selected: MultiTableConfigTable[]
  onSelectedChange: (tables: MultiTableConfigTable[]) => void
  /** Replace the whole selection with the clicked table instead of adding to
   * it — used by the missing-reference "Select alternative" flow (#970 Part C),
   * where exactly one replacement is being chosen. */
  singleSelect?: boolean
  searchPlaceholder?: string
  height?: number | string
  /**
   * 'columns' (#1176): the two-column layout PR #1137 built for the flat
   * editor — Selected Tables beside the tree, the picker growing to fill its
   * parent, so one picker design serves both surfaces and they cannot drift.
   * 'stacked' keeps the original compact shape (fixed-height tree with a chip
   * footer) for the small callers: the missing-reference "Select alternative"
   * flow and the agent-create panel.
   */
  layout?: 'stacked' | 'columns'
}

/**
 * Job-scoped table tree + selected tables. Single-job dashboards are locked
 * to their job (#961); multi-job ones (#1162) get a job selector above the
 * tree (#1229 C4) from the editor's DashboardJobsProvider. Shared by the wizard's Step 1 source browser
 * (columns layout), the missing-reference "Select alternative" picker and the
 * agent-create panel (stacked layout).
 */
export const TableSourcePicker: React.FC<TableSourcePickerProps> = ({
  customer,
  job,
  selected,
  onSelectedChange,
  singleSelect = false,
  searchPlaceholder = 'Search tables...',
  height = 260,
  layout = 'stacked',
}) => {
  const [expanded, setExpanded] = useState<string[]>([])
  const [treeSelected, setTreeSelected] = useState<string>('')
  // #1229 C4: on a multi-job dashboard the user picks the job first (the
  // selector renders only when the editor provides more than one job); the
  // tree and every table it adds belong to the picked job.
  const [pickJob, setPickJob] = useState(job)
  const treeJob = pickJob || job

  const tocQuery = useQuery({
    queryKey: ['toc', customer, treeJob],
    enabled: Boolean(customer && treeJob),
    queryFn: async () => {
      const res = await axios.get<{ toc: GenNode[] }>(`${API_BASE_URL}/job/${customer}/${treeJob}/toc`)
      return res.data.toc || []
    },
  })

  const rawToc = tocQuery.data || []
  const augmentedToc = useMemo(() => getAugementedTOC({ toc: rawToc, multiTables: [] }), [rawToc])
  const toc = augmentedToc.nodes

  // #1229 B: open the folders the TOC flags expanded, once per loaded TOC
  // (render-phase ref guard — no effect needed, nothing to clean up).
  const expandedInitRef = useRef<GenNode[] | null>(null)
  if (toc.length > 0 && expandedInitRef.current !== toc) {
    expandedInitRef.current = toc
    setExpanded(getInitialExpandedIds(toc))
  }

  const isSelected = (fullPath: string) => selected.some(t => tableFullPath(t) === fullPath)

  const handleNodeSelectToggle = (itemId: string, node: GenNode, isNodeSelected: boolean) => {
    if (!isNodeSelected) return
    if (node.type !== 'Table') {
      setTreeSelected(itemId)
      return
    }
    const filename = node.value1 || ''
    const folderPath = node.value2 || ''
    const fullPath = folderPath ? `${folderPath}/${filename}` : filename

    const newTable: MultiTableConfigTable = { displayName: getNodeTitle(node), folderPath, filename, job: treeJob }

    if (singleSelect) {
      onSelectedChange([newTable])
    } else if (!isSelected(fullPath)) {
      onSelectedChange([...selected, newTable])
    }
    setTreeSelected(itemId)
  }

  const handleNodeExpandToggle = (itemId: string, _node: GenNode, isExpanded: boolean) => {
    setExpanded(isExpanded ? [...expanded, itemId] : expanded.filter(id => id !== itemId))
  }

  const handleRemove = (index: number) => {
    onSelectedChange(selected.filter((_, i) => i !== index))
  }

  const tree = (
    <>
      <DashboardJobSelect value={treeJob} onChange={(j) => { setPickJob(j); setTreeSelected('') }} />
      {tocQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Tree
          searchEnabled
          searchPlaceholder={searchPlaceholder}
          nodes={toc}
          expanded={expanded}
          selected={treeSelected}
          onNodeSelectToggle={handleNodeSelectToggle}
          onNodeExpandToggle={handleNodeExpandToggle}
        />
      )}
    </>
  )

  if (layout === 'columns') {
    // The flat editor's structure (MultiTableAnalysisWidget.tsx), not its
    // pixel values: the wizard step has more room, so the picker fills it.
    // Same delete control as the flat editor — one design in both places.
    return (
      <Paper variant="outlined" sx={{ flex: 1, minHeight: 260, display: 'flex', minWidth: 0 }}>
        <Box sx={{ width: 220, minWidth: 200, flexShrink: 0, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ p: 1.5, pb: 0.5, bgcolor: 'grey.50' }}>
            Selected Tables ({selected.length})
          </Typography>
          {selected.length === 0 ? (
            <Box sx={{ p: 1.5, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="caption">Click tables to add them</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {selected.map((t, index) => (
                <ListItem key={`${t.job || ''}/${tableFullPath(t)}`} divider sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={<Typography variant="body2" fontSize="0.8rem">{t.displayName}</Typography>}
                    secondary={tableFullPath(t)}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true, fontSize: '0.7rem' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => handleRemove(index)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 1, minWidth: 0 }}>
          {tree}
        </Box>
      </Paper>
    )
  }

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ height, overflow: 'auto', p: 1 }}>
        {tree}
      </Box>
      {!singleSelect && (
        <>
          <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.length === 0 ? (
              <Typography variant="caption" color="text.secondary">No tables selected yet.</Typography>
            ) : (
              selected.map((t, i) => (
                <Chip
                  key={`${t.job || ''}/${tableFullPath(t)}`}
                  size="small"
                  label={t.displayName}
                  onDelete={() => handleRemove(i)}
                />
              ))
            )}
          </Box>
        </>
      )}
    </Paper>
  )
}

export default TableSourcePicker
